import React, { useState } from 'react';

const F = "'Plus Jakarta Sans',sans-serif";
const SERIF = "'Times New Roman',Times,serif";

function bandColor(b) {
  const n = parseFloat(b);
  if (!n) return '#9aa3b8';
  return n >= 7.5 ? '#1a7a4a' : n >= 7 ? '#3a7a10' : n >= 6.5 ? '#b8860b' : n >= 6 ? '#b8620a' : '#c0392b';
}

// Render a string, italicising anything wrapped in double-quotes
function WithQuotes({ text, quoteColor }) {
  if (!text || !text.includes('"')) return <span>{text}</span>;
  const parts = text.split(/("(?:[^"\\]|\\.)*")/g);
  return (
    <span>
      {parts.map((part, i) =>
        /^".*"$/.test(part)
          ? <em key={i} style={{ fontFamily: SERIF, fontStyle:'italic', color: quoteColor || 'inherit', background:`${quoteColor}18`, padding:'0 3px', borderRadius:2 }}>{part}</em>
          : <span key={i}>{part}</span>
      )}
    </span>
  );
}

const CRITERIA_META = [
  { key:'TA',  label:'Task Achievement',      short:'TA'  },
  { key:'CC',  label:'Coherence & Cohesion',  short:'CC'  },
  { key:'LR',  label:'Lexical Resource',      short:'LR'  },
  { key:'GRA', label:'Grammar',               short:'GRA' },
];

// ── Paragraph comparison (Smart Writing mode) ────────────────────────────────

const SW_COLORS = {
  grammar:     { fg:'#c0392b', bg:'rgba(192,57,43,0.10)', border:'#c0392b' },
  punctuation: { fg:'#7b3f00', bg:'rgba(123,63,0,0.10)',  border:'#7b3f00' },
  vocabulary:  { fg:'#1a5fa8', bg:'rgba(26,95,168,0.10)', border:'#1a5fa8' },
  phrasing:    { fg:'#6b21a8', bg:'rgba(107,33,168,0.10)',border:'#6b21a8' },
  structure:   { fg:'#b8620a', bg:'rgba(184,98,10,0.10)', border:'#b8620a' },
  cohesion:    { fg:'#0a7a5a', bg:'rgba(10,122,90,0.10)', border:'#0a7a5a' },
};

function SwTooltip({ errorType, text }) {
  const c = SW_COLORS[errorType] || SW_COLORS.grammar;
  return (
    <span style={{ position:'absolute', bottom:'calc(100% + 8px)', left:'50%', transform:'translateX(-50%)', background:'#1a2a4a', color:'#fff', padding:'10px 14px', borderRadius:10, fontSize:11.5, lineHeight:1.6, width:220, zIndex:500, boxShadow:'0 4px 20px rgba(0,0,0,0.28)', whiteSpace:'normal', pointerEvents:'none' }}>
      {errorType && <span style={{ display:'block', fontWeight:700, fontSize:10, letterSpacing:'.08em', textTransform:'uppercase', color:c.fg, marginBottom:4 }}>{errorType}</span>}
      {text}
      <span style={{ position:'absolute', top:'100%', left:'50%', transform:'translateX(-50%)', border:'5px solid transparent', borderTopColor:'#1a2a4a', display:'block', width:0, height:0 }}/>
    </span>
  );
}

function ParagraphComparison({ originalWithErrors, improvedVersion, paraTitle }) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [mobileView,    setMobileView]    = useState('original');
  if (!originalWithErrors?.length && !improvedVersion?.length) return null;
  const orig = originalWithErrors || [];
  const impr = improvedVersion    || [];
  return (
    <div onClick={() => setActiveTooltip(null)}>
      <style>{`
        .sw-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        .sw-toggle{display:none;border-radius:8px;overflow:hidden;border:1px solid #e8edf5;margin-bottom:12px;}
        @media(max-width:768px){.sw-grid{grid-template-columns:1fr;}.sw-toggle{display:flex;}.sw-hide{display:none!important;}}
      `}</style>

      <div style={{ marginBottom:10 }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:3 }}>
          📝 Your Paragraph vs Improved
        </div>
        <div style={{ fontSize:12.5, color:'#5a6a88' }}>Tap any highlighted text to see your tutor's explanation.</div>
      </div>

      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:12, padding:'8px 12px', background:'#f8f9fb', border:'1px solid #e8edf5', borderRadius:8 }}>
        <span style={{ fontSize:11.5, color:'#5a6a88', fontWeight:500 }}>Tap to explain:</span>
        <span style={{ fontSize:11.5, color:'#c0392b', background:'rgba(192,57,43,0.08)', borderBottom:'2px solid #c0392b', padding:'1px 7px', borderRadius:3 }}>needs improvement</span>
        <span style={{ fontSize:11.5, color:'#1a7a4a', background:'rgba(26,122,74,0.08)', borderBottom:'2px solid #1a7a4a', padding:'1px 7px', borderRadius:3 }}>improved</span>
      </div>

      <div className="sw-toggle">
        <button onClick={e=>{e.stopPropagation();setMobileView('original');}} style={{ flex:1, padding:'9px 0', background:mobileView==='original'?'#c0392b':'#fff', color:mobileView==='original'?'#fff':'#5a6a88', border:'none', cursor:'pointer', fontSize:13, fontWeight:mobileView==='original'?600:400 }}>Your Version</button>
        <button onClick={e=>{e.stopPropagation();setMobileView('improved');}} style={{ flex:1, padding:'9px 0', background:mobileView==='improved'?'#1a7a4a':'#fff', color:mobileView==='improved'?'#fff':'#5a6a88', border:'none', cursor:'pointer', fontSize:13, fontWeight:mobileView==='improved'?600:400 }}>Improved Version</button>
      </div>

      <div className="sw-grid">
        {/* LEFT — Student version with error highlights */}
        <div className={mobileView==='improved'?'sw-hide':''} style={{ background:'#fff', border:'1px solid #fecdd3', borderRadius:12, overflow:'hidden', boxShadow:'0 2px 8px rgba(192,57,43,0.07)' }}>
          <div style={{ background:'#fff5f5', borderBottom:'1px solid #fecdd3', padding:'11px 16px', display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:9, height:9, borderRadius:'50%', background:'#c0392b', flexShrink:0 }}/>
            <div style={{ fontSize:13, fontWeight:700, color:'#c0392b' }}>Your Version</div>
            {paraTitle && <div style={{ marginLeft:'auto', fontSize:10.5, color:'#9aa3b8', fontFamily:"'JetBrains Mono',monospace" }}>{paraTitle}</div>}
          </div>
          <div style={{ padding:'16px 18px', fontSize:14, lineHeight:2.1, fontFamily:"'Times New Roman',serif", color:'#1a1a1a' }}>
            {orig.length > 0 ? orig.map((seg, i) => {
              const key = `sw-o-${i}`;
              const isAct = activeTooltip === key;
              const c = SW_COLORS[seg.errorType] || SW_COLORS.grammar;
              if (!seg.hasError) return <span key={i}>{seg.text}</span>;
              return (
                <span key={i} style={{ position:'relative', display:'inline' }}>
                  <span onClick={e=>{e.stopPropagation();setActiveTooltip(isAct?null:key);}} style={{ background:c.bg, borderBottom:`2px solid ${c.border}`, color:c.fg, cursor:'pointer', borderRadius:2, padding:'0 1px' }}>{seg.text}</span>
                  {isAct && <SwTooltip errorType={seg.errorType} text={seg.explanation}/>}
                </span>
              );
            }) : <span style={{ color:'#9aa3b8', fontSize:13, fontFamily:F }}>No annotation available for this paragraph.</span>}
          </div>
        </div>

        {/* RIGHT — Improved version with correction highlights */}
        <div className={mobileView==='original'?'sw-hide':''} style={{ background:'#fff', border:'1px solid #bbf7d0', borderRadius:12, overflow:'hidden', boxShadow:'0 2px 8px rgba(26,122,74,0.07)' }}>
          <div style={{ background:'#f0fdf4', borderBottom:'1px solid #bbf7d0', padding:'11px 16px', display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:9, height:9, borderRadius:'50%', background:'#1a7a4a', flexShrink:0 }}/>
            <div style={{ fontSize:13, fontWeight:700, color:'#1a7a4a' }}>✨ Improved Version</div>
          </div>
          <div style={{ padding:'16px 18px', fontSize:14, lineHeight:2.1, fontFamily:"'Times New Roman',serif", color:'#1a1a1a' }}>
            {impr.length > 0 ? impr.map((seg, i) => {
              const key = `sw-i-${i}`;
              const isAct = activeTooltip === key;
              if (!seg.isCorrected) return <span key={i}>{seg.text}</span>;
              return (
                <span key={i} style={{ position:'relative', display:'inline' }}>
                  <span onClick={e=>{e.stopPropagation();setActiveTooltip(isAct?null:key);}} style={{ background:'rgba(26,122,74,0.10)', borderBottom:'2px solid #1a7a4a', color:'#1a7a4a', cursor:'pointer', borderRadius:2, padding:'0 1px' }}>{seg.text}</span>
                  {isAct && <SwTooltip text={seg.explanation}/>}
                </span>
              );
            }) : <span style={{ color:'#9aa3b8', fontSize:13, fontFamily:F }}>No annotation available for this paragraph.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Exported: used in FeedbackDisplay AND TestMode ───────────────────────────

export function CriterionCard({ criterionKey, criterion, label }) {
  const [open, setOpen] = useState(false);
  if (!criterion) return null;

  const col        = bandColor(criterion.score);
  const strengths  = Array.isArray(criterion.strengths)     ? criterion.strengths     : criterion.strengths     ? [criterion.strengths]     : [];
  const weaknesses = Array.isArray(criterion.weaknesses)    ? criterion.weaknesses    : criterion.weaknesses    ? [criterion.weaknesses]    : [];
  const howTo      = Array.isArray(criterion.how_to_improve)? criterion.how_to_improve: criterion.how_to_improve? [criterion.how_to_improve]: [];

  return (
    <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, overflow:'hidden', marginBottom:12, boxShadow:'0 1px 6px rgba(26,42,74,0.06)' }}>

      {/* Header: key + score + label + comment */}
      <div style={{ padding:'12px 16px', background:'#f8f9fb', borderBottom:'1px solid #e8edf5', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.12em', color:'#9aa3b8', textTransform:'uppercase', flexShrink:0 }}>{criterionKey}</span>
        <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:700, color:col, lineHeight:1, flexShrink:0 }}>{criterion.score}</span>
        <span style={{ fontSize:13, fontWeight:600, color:'#1a2a4a' }}>{label}</span>
        {criterion.comment && (
          <span style={{ fontSize:12, color:'#5a6a88', marginLeft:'auto', maxWidth:260, textAlign:'right', lineHeight:1.5 }}>{criterion.comment}</span>
        )}
      </div>

      {/* Why this score */}
      {criterion.why_score && (
        <div style={{ padding:'10px 16px', background:'#eff6ff', borderBottom:'1px solid #dbeafe' }}>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9.5, letterSpacing:'.1em', textTransform:'uppercase', color:'#1a5fa8', fontWeight:700, marginBottom:5 }}>Why this score</div>
          <div style={{ fontSize:13, color:'#1e3a5f', lineHeight:1.75 }}>{criterion.why_score}</div>
        </div>
      )}

      {/* Strengths */}
      {strengths.length > 0 && (
        <div style={{ padding:'10px 16px', background:'#f0fdf4', borderBottom:'1px solid #dcfce7' }}>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9.5, letterSpacing:'.1em', textTransform:'uppercase', color:'#1a7a4a', fontWeight:700, marginBottom:6 }}>✓ What you did well</div>
          {strengths.map((s, i) => (
            <div key={i} style={{ fontSize:13, color:'#14532d', lineHeight:1.75, marginBottom: i < strengths.length-1 ? 6 : 0 }}>
              <WithQuotes text={s} quoteColor="#166534"/>
            </div>
          ))}
        </div>
      )}

      {/* Weaknesses */}
      {weaknesses.length > 0 && (
        <div style={{ padding:'10px 16px', background:'#fffbeb', borderBottom:'1px solid #fef3c7' }}>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9.5, letterSpacing:'.1em', textTransform:'uppercase', color:'#b8620a', fontWeight:700, marginBottom:6 }}>✗ What limited your score</div>
          {weaknesses.map((w, i) => (
            <div key={i} style={{ fontSize:13, color:'#78350f', lineHeight:1.75, marginBottom: i < weaknesses.length-1 ? 6 : 0 }}>
              <WithQuotes text={w} quoteColor="#92400e"/>
            </div>
          ))}
        </div>
      )}

      {/* How to improve — collapsible */}
      {howTo.length > 0 && (
        <>
          <button onClick={() => setOpen(o => !o)}
            style={{ width:'100%', background:open?'#f0f4f8':'transparent', border:'none', borderTop:'1px solid #e8edf5', padding:'9px 16px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', fontFamily:F, color:'#5a6a88', fontSize:12.5, fontWeight:500, textAlign:'left' }}>
            <span>💡 How to Improve</span>
            <span style={{ fontSize:10, transform:open?'rotate(180deg)':'none', transition:'transform .2s', color:'#9aa3b8' }}>▼</span>
          </button>
          {open && (
            <div style={{ background:'#f8f9fb', borderTop:'1px solid #e8edf5', padding:'12px 16px' }}>
              {howTo.map((tip, i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom: i < howTo.length-1 ? 10 : 0 }}>
                  <div style={{ width:20, height:20, borderRadius:'50%', background:'#1a2a4a', color:'#d4a017', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0, fontFamily:"'JetBrains Mono',monospace" }}>{i+1}</div>
                  <div style={{ fontSize:13, color:'#2a3a5a', lineHeight:1.65, flex:1 }}>{tip}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function RepeatedVocabulary({ vocab }) {
  if (!Array.isArray(vocab) || vocab.length === 0) return null;
  return (
    <div style={{ padding:'20px 18px', borderBottom:'1px solid #e8edf5' }}>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>📚 Vocabulary to Vary</div>
      <div style={{ fontSize:12.5, color:'#5a6a88', marginBottom:14 }}>These words appear too frequently. Varying your vocabulary raises your Lexical Resource band score.</div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {vocab.map((entry, i) => (
          <div key={i} style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:10, padding:'12px 16px', boxShadow:'0 1px 4px rgba(26,42,74,0.05)', display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
            <div style={{ flexShrink:0 }}>
              <span style={{ background:'rgba(184,134,11,0.12)', color:'#b8860b', fontWeight:700, fontSize:13.5, padding:'3px 10px', borderRadius:6, border:'1px solid rgba(184,134,11,0.3)', fontFamily:"'JetBrains Mono',monospace" }}>
                {entry.word}
              </span>
              <span style={{ display:'block', fontSize:10.5, color:'#9aa3b8', marginTop:4, textAlign:'center' }}>used {entry.count}×</span>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:'#9aa3b8', marginBottom:6 }}>Try instead:</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {(entry.alternatives || []).map((alt, j) => (
                  <span key={j} style={{ background:'#f0f4f8', color:'#1a2a4a', fontSize:12.5, padding:'4px 12px', borderRadius:100, border:'1px solid #dde3ed', fontWeight:500 }}>{alt}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Improvement card (paragraph-level corrections) ────────────────────────────
function ImprovementCard({ imp, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, overflow:'hidden', marginBottom:12, boxShadow:'0 1px 6px rgba(26,42,74,0.06)' }}>
      <div style={{ padding:'14px 16px' }}>
        <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.12em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:10 }}>
          Improvement {index + 1}
        </div>
        <div style={{ background:'#fff5f5', border:'1.5px solid rgba(192,57,43,0.2)', borderRadius:8, padding:'10px 14px', marginBottom:8 }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:'#c0392b', fontFamily:F, letterSpacing:'.06em', marginBottom:5 }}>✗ WHAT YOU WROTE:</div>
          <div style={{ fontSize:14, color:'#8b1a1a', fontFamily:SERIF, lineHeight:1.7, wordBreak:'break-word' }}>"{imp.original}"</div>
        </div>
        <div style={{ background:'#f0faf4', border:'1.5px solid rgba(26,122,74,0.25)', borderRadius:8, padding:'10px 14px' }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:'#1a7a4a', fontFamily:F, letterSpacing:'.06em', marginBottom:5 }}>✓ WRITE THIS INSTEAD:</div>
          <div style={{ fontSize:14, color:'#0a4a2a', fontFamily:SERIF, lineHeight:1.7, wordBreak:'break-word' }}>"{imp.improved}"</div>
        </div>
      </div>
      <div style={{ background:'#fffaf0', borderTop:'1px solid #f0e8c8', padding:'8px 16px', display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ fontSize:14, flexShrink:0 }}>💡</span>
        <span style={{ fontSize:13, color:'#7a5a00', fontFamily:F, fontWeight:500, flex:1 }}>{imp.what}</span>
      </div>
      <button onClick={() => setOpen(o => !o)}
        style={{ width:'100%', background:open?'#f0f4f8':'transparent', border:'none', borderTop:'1px solid #e8edf5', padding:'9px 16px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', fontFamily:F, color:'#5a6a88', fontSize:12.5, fontWeight:500, textAlign:'left' }}>
        <span>Why this matters</span>
        <span style={{ fontSize:11, transform:open?'rotate(180deg)':'none', transition:'transform .2s', color:'#9aa3b8' }}>▼</span>
      </button>
      {open && (
        <div style={{ background:'#f8f9fb', borderTop:'1px solid #e8edf5', padding:'14px 16px' }}>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:10.5, fontWeight:700, color:'#1a5fa8', fontFamily:F, letterSpacing:'.08em', textTransform:'uppercase', marginBottom:4 }}>Why it matters:</div>
            <div style={{ fontSize:13, color:'#2a3a5a', fontFamily:F, lineHeight:1.7 }}>{imp.why}</div>
          </div>
          <div>
            <div style={{ fontSize:10.5, fontWeight:700, color:'#b8620a', fontFamily:F, letterSpacing:'.08em', textTransform:'uppercase', marginBottom:4 }}>The rule:</div>
            <div style={{ fontSize:13, color:'#2a3a5a', fontFamily:F, lineHeight:1.7 }}>{imp.rule}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FeedbackDisplay({ feedback, title }) {
  if (!feedback) return null;

  const currentBand   = feedback.currentBand  || feedback.band || '—';
  const potentialBand = feedback.potentialBand || (parseFloat(feedback.band || 0) + 0.5).toFixed(1) || '—';
  const improvements  = Array.isArray(feedback.improvements)    ? feedback.improvements    : [];
  const tips          = Array.isArray(feedback.topThreeTips)    ? feedback.topThreeTips    : [];
  const vocab         = Array.isArray(feedback.repeated_vocabulary) ? feedback.repeated_vocabulary : [];
  const rewrite       = feedback.rewrite || '';
  const trialBoost    = feedback.trialBoost;

  const [copied, setCopied] = useState(false);

  function buildPlainText() {
    const lines = ['IELTSCOMPASS — Essay Feedback', `Date: ${new Date().toLocaleDateString()}`, ''];
    lines.push(`Your Score Today: Band ${currentBand}`);
    lines.push(`Your Potential:   Band ${potentialBand}`);
    lines.push('');
    CRITERIA_META.forEach(c => {
      const cr = feedback[c.key];
      if (cr) lines.push(`${c.label}: ${cr.score} — ${cr.comment || ''}`);
    });
    lines.push('');
    if (improvements.length) {
      lines.push('How to Improve:');
      improvements.forEach((imp, i) => {
        lines.push(`${i+1}. ${imp.what}`);
        lines.push(`   ✗ "${imp.original}"`);
        lines.push(`   ✓ "${imp.improved}"`);
      });
    }
    lines.push('');
    if (tips.length) { lines.push('3 Things to Practise:'); tips.forEach((t,i)=>lines.push(`${i+1}. ${t}`)); }
    lines.push(''); lines.push('Navigate your path to success — wa.me/201008182425');
    return lines.join('\n');
  }

  function copyFeedback() {
    try {
      navigator.clipboard.writeText(buildPlainText()).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2500); });
    } catch {}
  }

  // Check if any criterion has the new detailed fields
  const hasDetailedCriteria = CRITERIA_META.some(c => feedback[c.key]?.why_score);
  const originalWithErrors  = Array.isArray(feedback.original_with_errors) ? feedback.original_with_errors : [];
  const improvedVersion     = Array.isArray(feedback.improved_version)     ? feedback.improved_version     : [];
  const hasComparison = originalWithErrors.length > 0 || improvedVersion.length > 0;

  return (
    <div style={{ fontFamily:F, borderRadius:14, overflow:'hidden', border:'1px solid #e8edf5', boxShadow:'0 2px 14px rgba(26,42,74,0.10)' }}>

      {/* ── HEADER: Overall scores + compact criteria badges ── */}
      <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', padding:'20px 22px' }}>
        {title && (
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.18em', textTransform:'uppercase', color:'rgba(255,255,255,0.5)', marginBottom:12 }}>
            {title} — AI Feedback
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div style={{ background:'rgba(255,255,255,0.08)', border:'1.5px solid rgba(255,255,255,0.15)', borderRadius:12, padding:'16px', textAlign:'center' }}>
            <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.5)', marginBottom:6 }}>Your Score Today</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:42, fontWeight:700, color:'#c8d0e0', lineHeight:1 }}>{currentBand}</div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>IELTS Band</div>
          </div>
          <div style={{ background:'rgba(184,134,11,0.15)', border:'1.5px solid rgba(184,134,11,0.4)', borderRadius:12, padding:'16px', textAlign:'center' }}>
            <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(212,160,23,0.8)', marginBottom:6 }}>Your Potential ✨</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:42, fontWeight:700, color:'#d4a017', lineHeight:1 }}>{potentialBand}</div>
            <div style={{ fontSize:11, color:'rgba(212,160,23,0.6)', marginTop:4 }}>With improvements</div>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
          {CRITERIA_META.map(c => {
            const cr = feedback[c.key];
            if (!cr) return null;
            const col = bandColor(cr.score);
            return (
              <div key={c.key} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:9, padding:'10px 8px', textAlign:'center' }}>
                <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.4)', marginBottom:4 }}>{c.short}</div>
                <div style={{ fontSize:22, fontWeight:700, color:col, fontFamily:"'Cormorant Garamond',serif", lineHeight:1, marginBottom:4 }}>{cr.score}</div>
                <div style={{ fontSize:9.5, color:'rgba(255,255,255,0.55)', lineHeight:1.45 }}>{cr.comment}</div>
              </div>
            );
          })}
        </div>
        {trialBoost && (
          <div style={{ marginTop:12, background:'rgba(212,160,23,0.12)', border:'1px solid rgba(212,160,23,0.3)', borderRadius:8, padding:'9px 14px', fontSize:12, color:'rgba(255,255,255,0.75)', textAlign:'center' }}>
            Keep practising — your score improves with every essay! 💪
          </div>
        )}
      </div>

      {/* ── PARAGRAPH COMPARISON ── */}
      {hasComparison && (
        <div style={{ padding:'20px 18px', borderBottom:'1px solid #e8edf5' }}>
          <ParagraphComparison
            originalWithErrors={originalWithErrors}
            improvedVersion={improvedVersion}
            paraTitle={title}
          />
        </div>
      )}

      {/* ── DETAILED CRITERION BREAKDOWN ── */}
      {hasDetailedCriteria && (
        <div style={{ padding:'20px 18px', borderBottom:'1px solid #e8edf5' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Criterion Breakdown</div>
          <div style={{ fontSize:12.5, color:'#5a6a88', marginBottom:16 }}>Detailed analysis of each IELTS assessment criterion.</div>
          {CRITERIA_META.map(c => (
            <CriterionCard key={c.key} criterionKey={c.short} criterion={feedback[c.key]} label={c.label}/>
          ))}
        </div>
      )}

      {/* ── REPEATED VOCABULARY ── */}
      {vocab.length > 0 && <RepeatedVocabulary vocab={vocab}/>}

      {/* ── IMPROVEMENTS ── */}
      {improvements.length > 0 && (
        <div style={{ padding:'20px 18px', borderBottom:'1px solid #e8edf5' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>How to Improve Your Score</div>
          <div style={{ fontSize:12.5, color:'#5a6a88', marginBottom:16 }}>Copy these exact changes into your essay. Each one raises your band score.</div>
          {improvements.map((imp, i) => <ImprovementCard key={i} imp={imp} index={i}/>)}
        </div>
      )}

      {/* ── REWRITE ── */}
      {rewrite && (
        <div style={{ padding:'20px 18px', borderBottom:'1px solid #e8edf5' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>✎ Your Paragraph — Improved</div>
          <div style={{ fontSize:12.5, color:'#5a6a88', marginBottom:12 }}>This is what your paragraph looks like at Band 7.5</div>
          <div style={{ background:'#fff', border:'1px solid #c8ead8', borderLeft:'4px solid #1a7a4a', borderRadius:'0 10px 10px 0', padding:'16px 18px' }}>
            <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9.5, letterSpacing:'.12em', textTransform:'uppercase', color:'#1a7a4a', marginBottom:8 }}>Band 7.5 Version</div>
            <div style={{ fontSize:14.5, color:'#1a1a1a', fontFamily:SERIF, lineHeight:1.85 }}>{rewrite}</div>
          </div>
        </div>
      )}

      {/* ── TOP 3 TIPS ── */}
      {tips.length > 0 && (
        <div style={{ padding:'20px 18px', background:'#f8f9fb', borderBottom:'1px solid #e8edf5' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>🎯 3 Things to Practise Before Your Next Essay</div>
          <div style={{ fontSize:12.5, color:'#5a6a88', marginBottom:14 }}>Focus on these to make the biggest jump in your band score.</div>
          {tips.map((tip, i) => (
            <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom: i < tips.length-1 ? 12 : 0 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background:'#1a2a4a', color:'#d4a017', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0, fontFamily:"'JetBrains Mono',monospace" }}>{i+1}</div>
              <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:9, padding:'10px 14px', flex:1, boxShadow:'0 1px 4px rgba(26,42,74,0.05)' }}>
                <div style={{ fontSize:13.5, color:'#1a2233', lineHeight:1.65, fontFamily:F }}>{tip}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── COPY / PRINT ── */}
      <div style={{ display:'flex', gap:10, padding:'14px 18px', background:'#fff' }} className="no-print">
        <button onClick={copyFeedback}
          style={{ flex:1, background:copied?'#e8f5ee':'#fff', color:copied?'#1a7a4a':'#1a2233', border:`1.5px solid ${copied?'#c8ead8':'#e8edf5'}`, borderRadius:9, padding:'10px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:F, display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all .2s' }}>
          {copied ? '✓ Copied!' : '📋 Copy Feedback'}
        </button>
        <button onClick={() => window.print()}
          style={{ flex:1, background:'#fff', color:'#1a2233', border:'1.5px solid #e8edf5', borderRadius:9, padding:'10px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:F, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
          🖨️ Print / Save PDF
        </button>
      </div>
    </div>
  );
}
